import type { LoginCredentials, RegisterRequest } from "@playarena/shared";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { useGetCurrentUserQuery } from "@/store/api/userApi";
import { getErrorMessage } from "@/utils/errorUtils";

import type { RootState } from "../store";
import { useLoginMutation, useRegisterMutation, useLogoutMutation } from "../store/api/authApi";

export const useAuth = () => {
  const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Only fetch current user if we have a token but no user data
  // This handles page refresh scenarios
  const {
    data: userResponse,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated || !!user, // Skip if not authenticated OR we already have user data
  });

  const [loginMutation, { isLoading: isLoginLoading, error: loginError }] = useLoginMutation();
  const [registerMutation, { isLoading: isRegisterLoading, error: registerError }] =
    useRegisterMutation();
  const [logoutMutation] = useLogoutMutation();

  // Use user from state first, fallback to query response
  const currentUser = user || userResponse?.data || null;
  const isLoading = isUserLoading || isLoginLoading || isRegisterLoading;
  const error = loginError || registerError || userError;

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await loginMutation(credentials);
      if ("data" in result && result.data?.success) {
        return result.data;
      }
      throw new Error("Login failed");
    },
    [loginMutation]
  );

  const handleRegister = useCallback(
    async (data: RegisterRequest) => {
      const result = await registerMutation(data);
      if ("data" in result && result.data?.success) {
        return result.data;
      }
      throw new Error("Registration failed");
    },
    [registerMutation]
  );

  const handleLogout = useCallback(async () => {
    await logoutMutation();
    // Note: logout action is already dispatched in the mutation's onQueryStarted
  }, [logoutMutation]);

  const hasRole = useCallback(
    (role: string) => {
      return currentUser?.role === role;
    },
    [currentUser]
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return currentUser ? roles.includes(currentUser.role) : false;
    },
    [currentUser]
  );

  const getAuthErrorMessage = (error: any): string => {
    return getErrorMessage(error);
  };

  return {
    user: currentUser,
    token,
    isAuthenticated,
    isLoading,
    error: getAuthErrorMessage(error),
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    hasRole,
    hasAnyRole,
  };
};
